/**
 * BucketList — the idle-mode body of the Lieferanten Deklarationen AP.
 *
 * Renders:
 *   · Drop zone (file upload box)
 *   · Pinned "Nicht zugeordnet" bucket (only when it has docs)
 *   · One Bucket per selected supplier record
 *   · Settings shortcut (handled by parent — this component only emits)
 *
 * State source of truth:
 *   selectedRecordIds  ← supplier scope, populated by parent on AP entry
 *   pendingDocuments   ← React Query against /ap/supplier/documents
 *
 * The component itself is dumb wrt how data is fetched; it receives
 * already-resolved props plus a set of callbacks. SupplierAktionenTab
 * does the wiring.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconUpload } from "@tabler/icons-react"
import clsx from "clsx"
import type { ChangeEvent, DragEvent } from "react"
import { useRef, useState } from "react"
import type { PendingDocumentOut, QmsRecord } from "../../../types"
import { Bucket } from "./Bucket"
import { DOC_DRAG_MIME } from "./PendingDocumentRow"
import { UnassignedBucket } from "./UnassignedBucket"

interface SupplierEntry {
  recordId: string
  name: string
  pk: string | null
}

interface Props {
  suppliers: SupplierEntry[] // currently-selected supplier records (= one bucket each)
  allSuppliers: QmsRecord[] // for unassigned-row dropdown
  pkPrimitiveName: string | null
  namePrimitiveName: string | null
  pendingDocs: PendingDocumentOut[]
  downloadUrl: (docId: string) => string
  onUpload: (files: File[], targetRecordId?: string | null) => void
  onReassign: (docId: string, targetRecordId: string | null) => void
  onDelete: (docId: string) => void
  onImport: (recordId: string) => void
  onRemoveFromSelection: (recordId: string) => void
  isImporting: boolean
  importingRecordId: string | null
  newDocumentIds: Set<string>
}

export function BucketList(props: Props) {
  const { translate } = useTranslation()
  const {
    suppliers,
    allSuppliers,
    pkPrimitiveName,
    namePrimitiveName,
    pendingDocs,
    downloadUrl,
    onUpload,
    onReassign,
    onDelete,
    onImport,
    onRemoveFromSelection,
    isImporting,
    importingRecordId,
    newDocumentIds,
  } = props

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOverDropzone, setIsDragOverDropzone] = useState(false)

  // Group docs by target_record_id
  const docsByRecord = new Map<string | null, PendingDocumentOut[]>()
  for (const d of pendingDocs) {
    const k = d.target_record_id
    if (!docsByRecord.has(k)) docsByRecord.set(k, [])
    docsByRecord.get(k)!.push(d)
  }
  const unassignedDocs = docsByRecord.get(null) ?? []

  const handleDropzoneFiles = (files: File[]) => {
    if (files.length > 0) onUpload(files, null)
  }

  const handleDropzoneDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragOverDropzone(false)
    const docId = e.dataTransfer.getData(DOC_DRAG_MIME)
    if (docId) {
      // Dragging an existing doc to the upload box → unassign.
      onReassign(docId, null)
      return
    }
    const files = Array.from(e.dataTransfer.files ?? [])
    handleDropzoneFiles(files)
  }

  const handleDropzoneDragOver = (e: DragEvent) => {
    if (
      e.dataTransfer.types.includes(DOC_DRAG_MIME) ||
      e.dataTransfer.types.includes("Files")
    ) {
      e.preventDefault()
      setIsDragOverDropzone(true)
    }
  }

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    handleDropzoneFiles(files)
    e.target.value = ""
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      {/* Drop zone */}
      <div
        onDrop={handleDropzoneDrop}
        onDragOver={handleDropzoneDragOver}
        onDragLeave={() => setIsDragOverDropzone(false)}
        className={clsx(
          "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 transition-colors",
          isDragOverDropzone
            ? "border-yellow1 bg-yellow1/5"
            : "border-grey4 bg-grey3/40 hover:border-grey5",
        )}
      >
        <div className="rounded-full bg-grey3 p-3">
          <IconUpload size={20} className="text-grey5" />
        </div>
        <p className="text-sm font-medium text-white">
          {translate("QMS/SUPPLIER/UPLOAD_DOCUMENT")}
        </p>
        <p className="text-xs text-grey5">
          {translate("QMS/SUPPLIER/UPLOAD_HINT")}
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="mt-1 rounded-md bg-yellow1 px-3 py-1.5 text-xs font-medium text-black hover:bg-yellow5/80"
        >
          {translate("QMS/SUPPLIER/CHOOSE_FILE")}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.pdf"
          multiple
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* Unassigned bucket (pinned at top, only when populated) */}
      <UnassignedBucket
        docs={unassignedDocs}
        suppliers={allSuppliers}
        pkPrimitiveName={pkPrimitiveName}
        namePrimitiveName={namePrimitiveName}
        downloadUrl={downloadUrl}
        onAssign={(docId, recordId) => onReassign(docId, recordId)}
        onAcceptFiles={(files) => onUpload(files, null)}
        onAcceptDocReassign={(docId) => onReassign(docId, null)}
        onDeleteDoc={onDelete}
        newDocumentIds={newDocumentIds}
      />

      {/* Section header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-grey5">
          {translate("QMS/SUPPLIER/BUCKETS_SELECTED", {
            count: suppliers.length,
            supplierLabel:
              suppliers.length === 1
                ? translate("QMS/SUPPLIER/SUPPLIER_SINGULAR")
                : translate("QMS/SUPPLIER/SUPPLIER_PLURAL"),
          })}
        </h3>
      </div>

      {/* Bucket list */}
      {suppliers.length === 0 ? (
        <div className="rounded-lg border border-dashed border-grey4 bg-grey1 p-6 text-center">
          <p className="text-sm text-grey5">
            {translate("QMS/SUPPLIER/NO_SUPPLIERS_SELECTED")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {suppliers.map((s) => {
            const docs = docsByRecord.get(s.recordId) ?? []
            return (
              <Bucket
                key={s.recordId}
                recordName={s.name}
                recordPk={s.pk}
                docs={docs}
                downloadUrl={downloadUrl}
                onImport={() => onImport(s.recordId)}
                onRemoveFromSelection={() => onRemoveFromSelection(s.recordId)}
                onAcceptFiles={(files) => onUpload(files, s.recordId)}
                onAcceptDocReassign={(docId) => onReassign(docId, s.recordId)}
                onDeleteDoc={onDelete}
                isImporting={isImporting && importingRecordId === s.recordId}
                newDocumentIds={newDocumentIds}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
